import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { safeStorage } from "@/lib/storage";

/**
 * GDPR-friendly cookie banner. Session cookies (auth) are strictly necessary
 * and require no consent. This banner exists to inform the user, provide the
 * link to the Privacy Policy, and record their acknowledgement so we don't
 * re-nag them on every page load.
 *
 * Storage: localStorage key `gcu:cookie-ack` = "1" once acknowledged.
 * Reset in the Privacy Policy footer if you want a "manage cookies" link.
 */
const ACK_KEY = "gcu:cookie-ack";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't slam the user before the page has rendered.
    const t = setTimeout(() => {
      if (!safeStorage.get(ACK_KEY)) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const ack = () => {
    safeStorage.set(ACK_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-30 rounded-xl shadow-lg"
      style={{
        background: "#FFFFFF",
        border: "1px solid var(--border-stone)",
        color: "var(--text-primary)",
      }}
      role="dialog"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
    >
      <div className="p-4 flex items-start gap-3">
        <div
          className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
          style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}
        >
          <Cookie className="w-4 h-4" />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-semibold mb-1">Cookies & your data</div>
          <p style={{ color: "var(--text-secondary)" }} className="leading-relaxed text-xs sm:text-sm">
            We use essential cookies to keep you signed in and your case data secure.
            We do not sell your data or use third-party advertising trackers. See our{" "}
            <Link to="/legal/privacy" className="underline" style={{ color: "var(--accent-copper)" }} data-testid="cookie-privacy-link">
              Privacy Policy
            </Link>{" "}
            for details.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={ack}
              className="btn-primary text-xs"
              data-testid="cookie-ack-btn"
            >
              Got it
            </button>
            <Link
              to="/legal/privacy"
              className="btn-ghost text-xs"
              style={{ border: "1px solid var(--border-stone)" }}
              data-testid="cookie-learn-more-link"
            >
              Learn more
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={ack}
          className="btn-ghost p-1 -mt-1 -mr-1 shrink-0"
          aria-label="Dismiss"
          data-testid="cookie-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
